import { createApiClient } from '../../../services/api/apiClient';

export interface ProposalHistorySummary {
  proposalId: string;
  clientName: string;
  clientSlug?: string;
  createdAt: Date;
  updatedAt?: Date;
  format?: string;
  teamMemberCount?: number;
  clientUrl?: string;
  metadataPath?: string;
  profileManifest?: Array<{
    userId: string;
    format: string;
    path: string;
    updatedAt?: Date;
  }>;
}

interface ProposalData {
  proposalId: string;
  clientName: string;
  clientSlug?: string;
  clientUrl?: string;
  profiles: any[];
  formatType: string;
  createdAt: string;
  updatedAt: string;
  teamMembers: string[];
  manual_context?: string;
  scraped_content?: string;
}

class ProposalApi {
  private async getApiClient(token: string, options?: any) {
    const { client } = createApiClient(
      process.env.REACT_APP_API_URL || 'http://localhost:9110',
      token,
      {
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
        enableRetry: true,
        enableLogging: process.env.NODE_ENV === 'development',
        ...options  // Allow overriding options
      }
    );

    return client;
  }

  async getProposalData(proposalId: string, token: string): Promise<ProposalData | null> {
    try {
      const client = await this.getApiClient(token);
      
      // Get metadata
      const metadataResponse = await client.get(
        `/api/proposal/proposals/id/${encodeURIComponent(proposalId)}/metadata`
      );

      if (!metadataResponse.data) {
        return null;
      }

      const metadata = metadataResponse.data;
      const profiles: any[] = [];
      
      // Get profiles for each team member
      for (const userId of metadata.team_members || []) {
        try {
          const profileResponse = await client.get(
            `/api/proposal/proposals/id/${encodeURIComponent(proposalId)}/profile/${userId}`,
            { params: { format_type: metadata.format } }
          );
          
          if (profileResponse.data) {
            profiles.push(profileResponse.data);
          }
        } catch (err) {
          console.warn(`Failed to fetch profile for user ${userId}:`, err);
        }
      }
      
      return {
        proposalId: metadata.proposal_id || proposalId,
        clientName: metadata.client_name,
        clientSlug: metadata.client_slug,
        clientUrl: metadata.client_url || '',
        profiles,
        formatType: metadata.format,
        createdAt: metadata.created_at,
        updatedAt: metadata.updated_at,
        teamMembers: metadata.team_members || [],
        manual_context: metadata.manual_context || '',
        scraped_content: metadata.scraped_content || ''
      };
    } catch (error) {
      console.error(`Error fetching proposal data for ${proposalId}:`, error);
      return null;
    }
  }

  async deleteProposal(proposalId: string, token: string): Promise<void> {
    try {
      const client = await this.getApiClient(token);
      await client.delete(`/api/proposal/proposals/id/${encodeURIComponent(proposalId)}`);
    } catch (error) {
      console.error(`Error deleting proposal ${proposalId}:`, error);
      throw new Error(`Failed to delete proposal ${proposalId}`);
    }
  }

  async listUserHistory(token: string, options: { forceRefresh?: boolean } = {}): Promise<ProposalHistorySummary[]> {
    try {
      const client = await this.getApiClient(token, {
        enableRetry: false,
        timeout: 30000
      });
      const response = await client.get('/api/proposal/proposals/user', {
        params: options.forceRefresh ? { refresh: true } : undefined
      });

      if (!Array.isArray(response.data)) {
        return [];
      }

      return response.data.map((item: any) => ({
        proposalId: item.proposal_id,
        clientName: item.client_name,
        clientSlug: item.client_slug,
        createdAt: new Date(item.created_at),
        updatedAt: item.updated_at ? new Date(item.updated_at) : undefined,
        format: item.format,
        teamMemberCount: item.team_member_count ?? (Array.isArray(item.team_member_ids) ? item.team_member_ids.length : 0),
        clientUrl: item.client_url,
        metadataPath: item.metadata_path,
        profileManifest: Array.isArray(item.profile_manifest)
          ? item.profile_manifest.map((entry: any) => ({
              userId: entry.user_id,
              format: entry.format,
              path: entry.path,
              updatedAt: entry.updated_at ? new Date(entry.updated_at) : undefined
            }))
          : undefined
      }));
    } catch (error) {
      console.error('Error fetching user proposal history:', error);
      throw new Error('Failed to fetch proposal history');
    }
  }

  async deleteUserProposal(proposalId: string, token: string): Promise<void> {
    try {
      const client = await this.getApiClient(token);
      await client.delete(`/api/proposal/proposals/user/${encodeURIComponent(proposalId)}`);
    } catch (error) {
      const status = (error as any)?.response?.status;
      if (status === 404) {
        console.warn(
          `Proposal ${proposalId} was already absent on the server; removing locally only.`
        );
        return;
      }

      console.error(`Error deleting proposal ${proposalId}:`, error);
      throw new Error('Failed to delete proposal');
    }
  }

  // ==================== PROMPT MANAGEMENT ====================

  async getPrompts(token: string): Promise<Record<string, string>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get('/api/proposal/prompts');
      return response.data;
    } catch (error) {
      console.error('Error fetching prompts:', error);
      throw new Error('Failed to fetch prompts');
    }
  }

  async getPrompt(formatType: string, token: string): Promise<string> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get(`/api/proposal/prompts/${formatType}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching prompt for ${formatType}:`, error);
      throw new Error('Failed to fetch prompt');
    }
  }

  async updatePrompt(formatType: string, content: string, token: string): Promise<void> {
    try {
      const client = await this.getApiClient(token);
      await client.put(`/api/proposal/prompts/${formatType}`, { content });
    } catch (error: any) {
      console.error(`Error updating prompt for ${formatType}:`, error);
      // Handle both old format (with errors array) and new format (simple string)
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail :
                          (detail?.errors?.join(', ') || 'Failed to update prompt');
      throw new Error(errorMessage);
    }
  }

  async invalidatePromptCache(token: string): Promise<void> {
    try {
      const client = await this.getApiClient(token);
      await client.post('/api/proposal/prompts/invalidate-cache');
    } catch (error) {
      console.error('Error invalidating prompt cache:', error);
      throw new Error('Failed to invalidate cache');
    }
  }

  // ==================== TEMPLATE MANAGEMENT ====================

  async listTemplateVersions(formatType: string, token: string): Promise<any[]> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get(`/api/proposal/templates/${formatType}/versions`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching template versions for ${formatType}:`, error);
      throw new Error('Failed to fetch template versions');
    }
  }

  async getCurrentTemplateInfo(formatType: string, token: string): Promise<any> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get(`/api/proposal/templates/${formatType}/current`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching current template info for ${formatType}:`, error);
      throw new Error('Failed to fetch current template info');
    }
  }

  async uploadTemplate(formatType: string, file: File, token: string): Promise<{ version: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const client = await this.getApiClient(token, {
        timeout: 120000  // 2 minutes for file upload
      });

      const response = await client.post(
        `/api/proposal/templates/${formatType}/upload`,
        formData,
        {
          headers: {
            'Content-Type': undefined  // Let browser set multipart/form-data with boundary
          }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error(`Error uploading template for ${formatType}:`, error);
      // Handle both old format (with errors array) and new format (simple string)
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail :
                          (detail?.errors?.join(', ') || 'Failed to upload template');
      throw new Error(errorMessage);
    }
  }

  async setTemplateVersion(formatType: string, versionName: string, token: string): Promise<void> {
    try {
      const client = await this.getApiClient(token);
      await client.put(`/api/proposal/templates/${formatType}/set-version`, {
        version_name: versionName
      });
    } catch (error) {
      console.error(`Error setting template version for ${formatType}:`, error);
      throw new Error('Failed to set template version');
    }
  }
}

export const proposalApi = new ProposalApi();
